/// <reference lib="dom" />
/**
 * Browser OpenTelemetry & RUM integration.
 *
 * Supports:
 * - Distributed tracing correlation via W3C `traceparent` (delegates to
 *   OtelRUM when active, falls back to standalone generator otherwise).
 * - Real User Monitoring (RUM) initialization matching the otel-debug contract.
 * - Error boundary exception reporting with React component stacks.
 */
const HEX = "0123456789abcdef";
function randomHex(bytes) {
    if (typeof crypto !== "undefined" && crypto.getRandomValues) {
        const arr = crypto.getRandomValues(new Uint8Array(bytes));
        let out = "";
        for (const b of arr) {
            out += HEX[b >> 4] + HEX[b & 0x0f];
        }
        return out;
    }
    // Math.random fallback for non-crypto contexts
    let out = "";
    for (let i = 0; i < bytes * 2; i++) {
        out += HEX[Math.floor(Math.random() * 16)];
    }
    return out;
}
/** Current page-level trace ID (reset on navigation if desired). */
let _traceId = randomHex(16);
/** Get the current trace ID (32 hex chars). */
export function getTraceId() {
    return _traceId;
}
/** Reset the trace ID (e.g. on SPA navigation). */
export function resetTraceId() {
    _traceId = randomHex(16);
    return _traceId;
}
/**
 * Generate a W3C `traceparent` header value.
 * Format: `00-{traceId}-{spanId}-01`
 */
export function generateTraceparent() {
    const spanId = randomHex(8);
    return `00-${_traceId}-${spanId}-01`;
}
/**
 * Returns W3C trace headers to spread into fetch/tRPC calls.
 * If OtelRUM is active, delegates to `rum.traceHeaders()`.
 * Otherwise falls back to standalone `traceparent` generation.
 *
 * ```ts
 * fetch("/api/foo", { headers: { ...traceHeaders(), ...otherHeaders } })
 * ```
 */
export function traceHeaders() {
    if (typeof window !== "undefined" && window.OtelRUM?.last?.traceHeaders) {
        try {
            return window.OtelRUM.last.traceHeaders();
        }
        catch {
            // fallback on error
        }
    }
    return { traceparent: generateTraceparent() };
}
/**
 * Capture an error from frontend code (e.g. React error boundary or catch block).
 */
export function captureFrontendException(error, attributes) {
    if (typeof window !== "undefined" && window.OtelRUM?.last?.captureException) {
        try {
            window.OtelRUM.last.captureException(error, attributes);
            return;
        }
        catch (e) {
            console.warn("[OtelRUM] Failed to capture exception:", e);
        }
    }
}
/**
 * Initialize OtelRUM by dynamically loading otel-rum.js from baseUrl
 * and calling OtelRUM.init().
 *
 * Resolves safely without throwing if baseUrl is not configured or if script fails.
 */
export async function initBrowserTelemetry(options = {}) {
    if (typeof window === "undefined")
        return;
    const baseUrl = options.baseUrl;
    if (!baseUrl) {
        // No OTEL URL configured; skip RUM
        return;
    }
    const cleanBase = baseUrl.replace(/\/+$/, "");
    const scriptUrl = `${cleanBase}/static/otel-rum.js`;
    await new Promise((resolve) => {
        if (window.OtelRUM) {
            try {
                window.OtelRUM.init({
                    serviceName: options.serviceName || "iamu-frontend",
                    release: options.release,
                    environment: options.environment,
                    baseUrl: cleanBase,
                    excludeTodo: options.excludeTodo ?? true,
                    ingestKey: options.ingestKey,
                    todo: options.todo,
                });
            }
            catch (err) {
                console.warn("[OtelRUM] Initialization error:", err);
            }
            return resolve();
        }
        const script = document.createElement("script");
        script.src = scriptUrl;
        script.async = true;
        script.crossOrigin = "anonymous";
        script.onload = () => {
            try {
                if (window.OtelRUM) {
                    window.OtelRUM.init({
                        serviceName: options.serviceName || "iamu-frontend",
                        release: options.release,
                        environment: options.environment,
                        baseUrl: cleanBase,
                        excludeTodo: options.excludeTodo ?? true,
                        ingestKey: options.ingestKey,
                        todo: options.todo,
                    });
                }
            }
            catch (err) {
                console.warn("[OtelRUM] Initialization error after load:", err);
            }
            resolve();
        };
        script.onerror = () => {
            console.warn(`[OtelRUM] Failed to load script from ${scriptUrl}`);
            resolve();
        };
        document.head.appendChild(script);
    });
}
//# sourceMappingURL=browser.js.map